import React, { useMemo, type ComponentType, type ReactNode } from 'react';

type ResolveCompoundSlotProps<TSlot> = (TSlot extends ComponentType<infer P>
  ? P
  : TSlot extends Record<string, ComponentType<infer P>>
  ? P
  : never) &
  object;

type UseResolveCompoundSlotAsIsParams<TRegistry extends object, TSlotKey extends keyof TRegistry> = {
  slotKey: TSlotKey;
  asIs: true;
  getter: (slotKey: TSlotKey) => TRegistry[TSlotKey] | null | undefined;
};

type UseResolveCompoundSlotElementParams<TRegistry extends object, TSlotKey extends keyof TRegistry> = {
  slotKey: TSlotKey;
  asIs?: false;
  props: ResolveCompoundSlotProps<TRegistry[TSlotKey]>;
  getter: (
    slotKey: TSlotKey,
    props: ResolveCompoundSlotProps<TRegistry[TSlotKey]>,
  ) => ComponentType<ResolveCompoundSlotProps<TRegistry[TSlotKey]>> | null | undefined;
};

export type UseResolveCompoundSlotParams<TRegistry extends object, TSlotKey extends keyof TRegistry> =
  | UseResolveCompoundSlotAsIsParams<TRegistry, TSlotKey>
  | UseResolveCompoundSlotElementParams<TRegistry, TSlotKey>;

/* eslint-disable no-redeclare -- TypeScript function overloads */
export function useResolveCompoundSlot<TRegistry extends object, TSlotKey extends keyof TRegistry>(
  params: UseResolveCompoundSlotAsIsParams<TRegistry, TSlotKey>,
): TRegistry[TSlotKey] | null | undefined;

export function useResolveCompoundSlot<TRegistry extends object, TSlotKey extends keyof TRegistry>(
  params: UseResolveCompoundSlotElementParams<TRegistry, TSlotKey>,
): ReactNode;

export function useResolveCompoundSlot<TRegistry extends object, TSlotKey extends keyof TRegistry>(
  params: UseResolveCompoundSlotParams<TRegistry, TSlotKey>,
): ReactNode | TRegistry[TSlotKey] | null | undefined {
  const asIs = params.asIs === true;
  const props = asIs ? undefined : params.props;
  const resolved = asIs ? params.getter(params.slotKey) : params.getter(params.slotKey, params.props);

  const result: ReactNode | TRegistry[TSlotKey] | null | undefined = useMemo(() => {
    if (asIs) return resolved as TRegistry[TSlotKey] | null | undefined;

    const Slot = resolved as ComponentType<ResolveCompoundSlotProps<TRegistry[TSlotKey]>> | null | undefined;

    return Slot && props ? React.createElement(Slot, props) : null;
  }, [asIs, props, resolved]);

  return result;
}
/* eslint-enable no-redeclare */
